import React, { useState, useRef } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Upload,
  Image as ImageIcon,
  Save,
  Link2,
  Type,
} from "lucide-react";

import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaLinkedinIn,
  FaYoutube,
} from "react-icons/fa";
import Teamslider from '../vendor/Teamslider.jsx';
/* ---------------------------------------------
   Default social icons shown centered on banner
--------------------------------------------- */
const SOCIAL_ICONS = [
  { key: "facebook", Icon: FaFacebookF, url: "https://facebook.com" },
  { key: "instagram", Icon: FaInstagram, url: "https://instagram.com" },
  { key: "twitter", Icon: FaTwitter, url: "https://twitter.com" },
  { key: "linkedin", Icon: FaLinkedinIn, url: "https://linkedin.com" },
  { key: "youtube", Icon: FaYoutube, url: "https://youtube.com" },
];

const EMPTY_FORM = {
  id: null,
  title: "",
  subtitle: "",
  link: "",
  image: "",
};

export default function BannerAdminManager() {
  const [banners, setBanners] = useState([
    {
      id: 1,
      title: "Welcome to APIC ProtoHub",
      subtitle: "Turning ideas into real innovations",
      link: "https://apic.in",
      image:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=700&fit=crop&auto=format&q=80",
    },
    
  ]);

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  /* ---------- Handlers ---------- */
  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setForm(banner);
    setIsEditing(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.image || !form.title.trim()) return;

    if (isEditing) {
      setBanners((prev) =>
        prev.map((b) => (b.id === form.id ? { ...form } : b))
      );
    } else {
      setBanners((prev) => [
        ...prev,
        { ...form, id: Date.now() },
      ]);
    }
    closeModal();
  };

  const confirmDelete = (id) => setDeleteTarget(id);
  const cancelDelete = () => setDeleteTarget(null);
  const executeDelete = () => {
    setBanners((prev) => prev.filter((b) => b.id !== deleteTarget));
    setDeleteTarget(null);
  };

  /* ---------- Render ---------- */
  return (
    <>
    <div className="bam-wrapper">
      {/* Header */}
      <div className="bam-header">
        
        
      </div>

      {/* Banner grid - Full width with 600px height */}
      {banners.length === 0 ? (
        <div className="bam-empty">
          <ImageIcon size={36} />
          <p>No banners added yet.</p>
          <button className="bam-add-btn" onClick={openAddModal}>
            <Plus size={18} />
            <span>Add your first banner</span>
          </button>
        </div>
      ) : (
        <div className="bam-grid">
          {banners.map((banner) => (
            <div key={banner.id} className="bam-card">
              <div className="bam-card-image-wrap">
                <img
                  src={banner.image}
                  alt={banner.title}
                  className="bam-card-image"
                />

                {/* Top action buttons */}
                <div className="bam-card-actions">
                    
                  <button
                    className="bam-icon-btn"
                    onClick={() => openEditModal(banner)}
                    aria-label="Edit banner"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="bam-icon-btn danger"
                    onClick={() => confirmDelete(banner.id)}
                    aria-label="Delete banner"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {/* Bottom gradient overlay */}
                <div className="bam-card-overlay">
                  <h3 className="bam-card-heading">{banner.title}</h3>
                  {banner.subtitle && (
                    <p className="bam-card-sub">{banner.subtitle}</p>
                  )}

                  {/* Centered social icons */}
                  <div className="bam-social-row">
                    {SOCIAL_ICONS.map(({ key, Icon, url }) => (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bam-social-icon"
                        aria-label={key}
                      >
                        <Icon size={16} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="bam-overlay" onClick={closeModal}>
          <div className="bam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bam-modal-header">
              <h3>{isEditing ? "Edit Banner" : "Add New Banner"}</h3>
              <button className="bam-close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form className="bam-form" onSubmit={handleSubmit}>
              {/* Image upload */}
              <label className="bam-label">
                <ImageIcon size={16} />
                Banner Image
              </label>

              <div
                className="bam-upload-box"
                onClick={() => fileInputRef.current?.click()}
              >
                {form.image ? (
                  <img
                    src={form.image}
                    alt="preview"
                    className="bam-upload-preview"
                  />
                ) : (
                  <div className="bam-upload-placeholder">
                    <Upload size={26} />
                    <span>Click to upload image</span>
                    <span className="bam-upload-hint">
                      Recommended size: 1600 × 700px
                    </span>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              <label className="bam-label">
                <Type size={16} />
                Title
              </label>
              <input
                type="text"
                className="bam-input"
                placeholder="Enter banner title"
                value={form.title}
                onChange={(e) =>
                  setForm((p) => ({ ...p, title: e.target.value }))
                }
                required
              />

              <label className="bam-label">
                <Type size={16} />
                Subtitle (optional)
              </label>
              <input
                type="text"
                className="bam-input"
                placeholder="Enter short subtitle"
                value={form.subtitle}
                onChange={(e) =>
                  setForm((p) => ({ ...p, subtitle: e.target.value }))
                }
              />

              <button type="submit" className="bam-submit-btn">
                <Save size={18} />
                <span>{isEditing ? "Save Changes" : "Add Banner"}</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="bam-overlay" onClick={cancelDelete}>
          <div
            className="bam-confirm-box"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bam-confirm-icon">
              <Trash2 size={22} />
            </div>
            <h4>Delete this banner?</h4>
            <p>This action cannot be undone.</p>
            <div className="bam-confirm-actions">
              <button className="bam-cancel-btn" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="bam-delete-btn" onClick={executeDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Styles ---------------- */}
      <style>{`
        * { box-sizing: border-box; }

        .bam-wrapper {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        //   max-width: 1400px;
        height:90vh;
        overflow:auto;
        scrollbar-width:none;

         
          padding:10px;
          color: #0B1F3A;
        }

        .bam-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
         
          flex-wrap: wrap;
        }

        .bam-title {
          font-size: 26px;
          font-weight: 800;
          margin: 0 0 6px 0;
          color: #0B1F3A;
        }

        .bam-subtitle {
          font-size: 14.5px;
          color: #64748B;
          margin: 0;
        }

        .bam-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #16A34A;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 12px 20px;
          font-size: 14.5px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
          white-space: nowrap;
        }
        .bam-add-btn:hover {
          background: #128a3e;
          transform: translateY(-1px);
        }

        .bam-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          padding: 70px 20px;
          border: 1.5px dashed #D6DEE8;
          border-radius: 16px;
          color: #64748B;
          text-align: center;
        }

        /* Full width grid - single column */
        .bam-grid {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        /* Each card takes full width with 600px height */
        .bam-card {
          background: #fff;
          border: 1px solid #E5EAF1;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(16,24,40,0.04);
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          width: 100%;
        }
        .bam-card:hover {
          box-shadow: 0 12px 28px -8px rgba(16,24,40,0.18);
          transform: translateY(-2px);
        }

        /* Image wrapper with fixed 600px height */
        .bam-card-image-wrap {
          position: relative;
          width: 100%;
          height: 450px;
          overflow: hidden;
          background: #0B1F3A;
        }

        .bam-card-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .bam-card-actions {
          position: absolute;
          top: 16px;
          right: 16px;
          display: flex;
          gap: 8px;
          z-index: 2;
        }

        .bam-icon-btn {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: rgba(255,255,255,0.95);
          color: #0B1F3A;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease, transform 0.15s ease;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .bam-icon-btn:hover {
          background: #fff;
          transform: scale(1.08);
        }
        .bam-icon-btn.danger:hover {
          background: #FEE2E2;
          color: #DC2626;
        }

        .bam-card-overlay {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 30px 24px 24px;
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.85) 100%);
          text-align: center;
        }

        .bam-card-heading {
          margin: 0 0 6px 0;
          font-size: 28px;
          font-weight: 800;
          color: #fff;
          line-height: 1.2;
          text-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }

        .bam-card-sub {
          margin: 0 0 16px 0;
          font-size: 16px;
          color: rgba(255,255,255,0.9);
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .bam-social-row {
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .bam-social-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.4);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: background 0.2s ease, transform 0.15s ease;
          backdrop-filter: blur(4px);
        }
        .bam-social-icon:hover {
          background: #16A34A;
          border-color: #16A34A;
          transform: translateY(-3px) scale(1.05);
        }

        .bam-card-footer {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 20px;
          font-size: 13px;
          color: #64748B;
          border-top: 1px solid #F0F3F8;
        }

        .bam-card-link {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ---------- Modal ---------- */
        .bam-overlay {
          position: fixed;
          inset: 0;
          background: rgba(11,31,58,0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .bam-modal {
          background: #fff;
          border-radius: 18px;
          width: 100%;
          max-width: 480px;
          max-height: 85vh;
          scrollbar-Width:none;
          overflow-y: auto;
          padding: 28px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }

        .bam-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .bam-modal-header h3 {
          margin: 0;
          font-size: 19px;
          font-weight: 800;
          color: #0B1F3A;
        }

        .bam-close-btn {
          background: #F1F5F9;
          border: none;
          border-radius: 50%;
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #475569;
          transition: background 0.2s ease;
        }
        .bam-close-btn:hover { background: #E2E8F0; }

        .bam-form {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .bam-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13.5px;
          font-weight: 700;
          color: #334155;
          margin: 14px 0 6px 0;
        }

        .bam-input {
          border: 1px solid #E2E8F0;
          border-radius: 10px;
          padding: 12px 14px;
          font-size: 14.5px;
          color: #0B1F3A;
          outline: none;
          transition: border-color 0.2s ease;
          width: 100%;
        }
        .bam-input:focus {
          border-color: #16A34A;
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
        }

        .bam-upload-box {
          border: 1.5px dashed #CBD5E1;
          border-radius: 12px;
          cursor: pointer;
          overflow: hidden;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .bam-upload-box:hover {
          border-color: #16A34A;
          background: #F5FBF7;
        }

        .bam-upload-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 36px 16px;
          color: #64748B;
          font-size: 14px;
          font-weight: 600;
        }
        .bam-upload-hint {
          font-size: 12px;
          font-weight: 400;
          color: #94A3B8;
        }

        .bam-upload-preview {
          width: 100%;
          max-height: 200px;
          object-fit: cover;
          display: block;
        }

        .bam-submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #0B1F3A;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 15px;
          font-weight: 700;
          margin-top: 20px;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .bam-submit-btn:hover { background: #142c52; }

        /* ---------- Delete confirm ---------- */
        .bam-confirm-box {
          background: #fff;
          border-radius: 16px;
          padding: 32px;
          max-width: 360px;
          width: 100%;
          text-align: center;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .bam-confirm-icon {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #FEE2E2;
          color: #DC2626;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 14px;
        }
        .bam-confirm-box h4 {
          margin: 0 0 6px 0;
          font-size: 18px;
          color: #0B1F3A;
        }
        .bam-confirm-box p {
          margin: 0 0 22px 0;
          font-size: 14px;
          color: #64748B;
        }
        .bam-confirm-actions {
          display: flex;
          gap: 12px;
        }
        .bam-cancel-btn, .bam-delete-btn {
          flex: 1;
          padding: 12px;
          border-radius: 10px;
          border: none;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .bam-cancel-btn {
          background: #F1F5F9;
          color: #334155;
        }
        .bam-cancel-btn:hover { background: #E2E8F0; }
        .bam-delete-btn {
          background: #DC2626;
          color: #fff;
        }
        .bam-delete-btn:hover { background: #b91c1c; }

        /* ---------- Responsive ---------- */
        @media (max-width: 1024px) {
          .bam-card-image-wrap {
            height: 500px;
          }
          .bam-card-heading {
            font-size: 24px;
          }
        }

        @media (max-width: 768px) {
          .bam-card-image-wrap {
            height: 400px;
          }
          .bam-card-heading {
            font-size: 20px;
          }
          .bam-card-sub {
            font-size: 14px;
          }
          .bam-social-icon {
            width: 34px;
            height: 34px;
          }
          .bam-card-actions {
            top: 12px;
            right: 12px;
          }
          .bam-icon-btn {
            width: 34px;
            height: 34px;
          }
        }

        @media (max-width: 640px) {
          .bam-wrapper {
            padding: 20px 12px 40px;
          }

          .bam-header {
            flex-direction: column;
          }
          .bam-add-btn {
            width: 100%;
            justify-content: center;
          }

          .bam-card-image-wrap {
            height: 300px;
          }
          .bam-card-heading {
            font-size: 18px;
          }
          .bam-card-sub {
            font-size: 13px;
          }
          .bam-card-overlay {
            padding: 20px 16px 16px;
          }
          .bam-social-icon {
            width: 30px;
            height: 30px;
          }
          .bam-social-icon svg {
            width: 14px;
            height: 14px;
          }
          .bam-modal {
            padding: 20px;
            max-width: 100%;
          }

          .bam-grid {
            gap: 20px;
          }
        }

        @media (max-width: 400px) {
          .bam-card-image-wrap {
            height: 250px;
          }
          .bam-card-heading {
            font-size: 16px;
          }
          .bam-card-sub {
            font-size: 12px;
          }
          .bam-social-icon {
            width: 26px;
            height: 26px;
          }
          .bam-social-icon svg {
            width: 12px;
            height: 12px;
          }
        }
      `}</style>
      <Teamslider/>
    </div>
    
    </>
  );
}